import { useMemo } from "react"
import { TableConfiguration } from "components/ui/tables/TableConfiguration/TableConfiguration"
import type { ColumnsType } from "antd/es/table"

type Props<TRow extends object, TSubCategories> = {
	subCategories: TSubCategories
	/** Builds the antd columns + rows for this category. Must be referentially stable. */
	buildTable: (subCategories: TSubCategories) => {
		columns: ColumnsType<TRow>
		data: TRow[]
		totalWidth?: number
	}
	scrollY?: number
	bordered?: boolean
	/** Render only the visible rows. See the note on TableConfiguration's `virtual`. */
	virtual?: boolean
}

/**
 * Renders one category's table, building its columns/rows **on mount** rather than
 * while the parent renders.
 *
 * The collapse panels only mount their children once expanded, so wrapping the
 * build in a component keeps a collapsed category free: with thousands of model
 * numbers, building the row data for every category on every parent render was
 * pure waste. The `useMemo` additionally keeps the built table stable across
 * re-renders, so opening a modal no longer rebuilds every row.
 */
export const CompetitorCategoryTable = <TRow extends object, TSubCategories>({
	subCategories,
	buildTable,
	scrollY,
	bordered = false,
	virtual = false,
}: Props<TRow, TSubCategories>) => {
	const { columns, data, totalWidth } = useMemo(() => buildTable(subCategories), [buildTable, subCategories])

	return (
		<TableConfiguration
			columns={columns}
			data={data}
			scrollY={scrollY}
			bordered={bordered}
			virtual={virtual}
			scrollX={totalWidth}
		/>
	)
}
